import type { TemplateResult } from 'lit';
import { LitElement } from 'lit';
import { defineLazyElement, type LazyElement, LazyElementsController } from '../lazy-elements/lazy-elements';
import { isTestingEnv } from '../test-utils/is-testing-env';
import { registerTestStylesElement } from '../test-utils/test-styles-element';

export type ImportedElement = typeof BcfLitElement;

type HtmlWithImportedElementsConfig = {
  id?: string;
  importedElements?: ImportedElement[];
  html: TemplateResult;
};

function registerElements(importedElements: ImportedElement[]): void {
  for (const elementToDefine of importedElements) {
    registerElement(elementToDefine);
  }
}

export function registerElement(elementToDefine: ImportedElement): void {
  if (elementToDefine.elementSelector.length === 0) {
    console.error('you need to define elementSelector property on TmLitElement class', elementToDefine);
    return;
  }
  // we cant have delay here because hydration can crash, we must wait for customElementRegistry scoped
  if (!customElements.get(elementToDefine.elementSelector)) {
    customElements.define(elementToDefine.elementSelector, elementToDefine as unknown as CustomElementConstructor);
  }
}

export function htmlWithImportedElements(config: HtmlWithImportedElementsConfig): TemplateResult {
  registerElements(config.importedElements ?? []);
  return config.html;
}

export abstract class BcfLitElement extends LitElement {
  protected get _importedElements(): ImportedElement[] {
    return [];
  }
  public static elementSelector: string = '';
  public static override shadowRootOptions: ShadowRootInit = { ...LitElement.shadowRootOptions, serializable: true };

  /** Child elements loaded on demand instead of at boot - triggers: see `LazyElementTrigger`. */
  protected get _lazyElements(): LazyElement[] {
    return [];
  }

  constructor() {
    super();
    registerElements(this._importedElements);
    if (__RENDER_MODE__ === 'browser') {
      const lazyElements: LazyElement[] = this._lazyElements;
      if (lazyElements.length > 0) {
        new LazyElementsController(this, lazyElements);
      }
    }
  }

  /** For `on: 'manual'` entries, e.g. to swap a placeholder only once the element is defined. */
  protected _defineLazyElement(tag: string): Promise<void> {
    const entry: LazyElement | undefined = this._lazyElements.find((lazy: LazyElement) => lazy.tag === tag);
    return entry ? defineLazyElement(entry) : Promise.reject(new Error(`${tag} is not declared in _lazyElements`));
  }

  protected _registerStylesForSnapshots(element: typeof BcfLitElement): void {
    if (isTestingEnv && element.styles) {
      registerTestStylesElement(element);
    }
  }
}

export abstract class BcfLitNoShadowElement extends BcfLitElement {
  public override createRenderRoot(): this {
    return this;
  }
}
